import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Clock, Eye, Share2, ChevronRight, Home as HomeIcon, MessageCircle, Facebook } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import CategoryBadge from "@/components/CategoryBadge";
import ArticleCard from "@/components/ArticleCard";
import AdSlot from "@/components/AdSlot";
import CommentsSection from "@/components/CommentsSection";
import RecommendedForYou from "@/components/RecommendedForYou";
import ArticleGallery from "@/components/ArticleGallery";
import Seo from "@/components/Seo";
import { recordRead } from "@/lib/reading";
import { formatDate, timeAgo, formatViews } from "@/lib/format";

export default function ArticlePage() {
  const { slug } = useParams();

  const { data: article, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => api.get(`/articles/${slug}`).then((r) => r.data),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (article?.slug) recordRead(article);
  }, [article]);

  // Share via the backend prerender URL so social crawlers (which don't run JS)
  // scrape the article's own title/image instead of the site default. Opening it
  // instantly redirects real visitors to the canonical article page.
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const shareUrl = article?.slug ? `${backend}/api/share/${article.slug}` : "";

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: article?.title, url: shareUrl });
      } catch (e) {
        /* cancelled */
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Връзката е копирана");
    }
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank",
      "noopener,noreferrer,width=640,height=640"
    );
  };

  const shareX = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article?.title || "")}`,
      "_blank",
      "noopener,noreferrer,width=640,height=640"
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (isError || !article) {
    return (
      <Layout>
        <Seo title="Статията не е намерена" noindex />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">
            Статията не е намерена
          </h1>
          <p className="mt-3 text-slate-600">
            Възможно е връзката да е грешна или статията да е премахната.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0041A8]"
          >
            Към началната страница
          </Link>
        </div>
      </Layout>
    );
  }

  const paragraphs = (article.content || "").split("\n").filter((p) => p.trim());
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.excerpt,
    image: article.image_url ? [article.image_url] : undefined,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Person", name: article.author || "Редакция" },
    publisher: {
      "@type": "NewsMediaOrganization",
      name: "Подуяне Днес",
      url: origin,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${origin}/article/${article.slug}`,
    },
    articleSection: article.category,
    inLanguage: "bg-BG",
  };

  return (
    <Layout>
      <Seo
        title={article.title}
        description={article.excerpt}
        image={article.image_url}
        type="article"
        jsonLd={articleJsonLd}
      />
      <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8" data-testid="article-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-slate-500">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-primary">
            <HomeIcon className="h-3.5 w-3.5" />
            Начало
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-700">{article.category}</span>
        </nav>

        <div className="mt-4">
          <CategoryBadge category={article.category} />
        </div>

        <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl" data-testid="article-title">
          {article.title}
        </h1>

        <p className="mt-4 text-lg leading-relaxed text-slate-600">{article.excerpt}</p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-slate-200 py-3">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="font-semibold text-slate-700">{article.author}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {timeAgo(article.published_at)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {formatViews(article.views)}
            </span>
            <span className="inline-flex items-center gap-1" data-testid="article-comment-count">
              <MessageCircle className="h-4 w-4" />
              {article.comment_count ?? 0}
            </span>
            <span>{article.read_time} мин четене</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={shareFacebook}
              data-testid="article-share-facebook"
              aria-label="Сподели във Facebook"
              title="Сподели във Facebook"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-[#1877F2] hover:text-[#1877F2]"
            >
              <Facebook className="h-4 w-4" />
            </button>
            <button
              onClick={shareX}
              data-testid="article-share-x"
              aria-label="Сподели в X"
              title="Сподели в X"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-slate-900 hover:text-slate-900"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-[15px] w-[15px]">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </button>
            <button
              onClick={share}
              data-testid="article-share-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-primary hover:text-primary"
            >
              <Share2 className="h-4 w-4" />
              Сподели
            </button>
          </div>
        </div>

        <div className="my-6 overflow-hidden rounded-2xl bg-slate-100">
          <img
            src={article.image_url}
            alt={article.image_alt || article.title}
            fetchPriority="high"
            decoding="async"
            className="aspect-video w-full object-cover"
          />
          {article.image_alt && (
            <p className="px-4 py-2 text-xs text-slate-500">{article.image_alt}</p>
          )}
        </div>

        <AdSlot placement="article_top" className="mb-6" />

        <div className="article-body text-[17px] text-slate-800" data-testid="article-content">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {article.gallery?.length > 0 && <ArticleGallery items={article.gallery} />}

        <div className="mt-6 text-xs text-slate-400">
          Публикувано на {formatDate(article.published_at)}
        </div>

        <AdSlot placement="article_bottom" className="mt-8" />

        {/* Related */}
        {article.related?.length > 0 && (
          <section className="mt-14">
            <h2 className="mb-5 font-display text-2xl font-extrabold text-slate-900">
              Още от „{article.category}“
            </h2>
            <div className="grid gap-6 sm:grid-cols-3">
              {article.related.map((a) => (
                <ArticleCard key={a.id} article={a} variant="default" />
              ))}
            </div>
          </section>
        )}

        <RecommendedForYou
          seedCategory={article.category}
          excludeSlugs={[article.slug, ...(article.related || []).map((r) => r.slug)]}
          className="mt-14"
        />

        <CommentsSection slug={article.slug} />
      </article>
    </Layout>
  );
}
